import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Sparkles, Menu, X, MessageCircle } from "lucide-react";
import Marquee from "react-fast-marquee";
import { useAuth } from "@/context/AuthContext";
import { fmtEuros } from "@/lib/format";

export const AnnouncementBar = () => (
  <div className="bg-gold text-[#201a08] overflow-hidden border-b border-[#201a08]/10" data-testid="announcement-bar">
    <Marquee speed={55} gradient={false} autoFill pauseOnHover={false} className="py-2.5 md:py-3">
      <span className="mx-10 text-sm md:text-base font-bold tracking-wide inline-flex items-center gap-2.5">
        <Sparkles className="w-4 h-4 md:w-5 md:h-5" strokeWidth={2} /> 🎁 Offre découverte : votre 1<sup>ère</sup> consultation, les 5 premières minutes sont OFFERTES — sans carte bancaire
      </span>
    </Marquee>
  </div>
);

export const BrandMark = ({ className = "", night = false }) => (
  <span className={`flex items-center gap-2 ${className}`}>
    <Sparkles className="w-5 h-5 text-goldsoft" strokeWidth={1.5} />
    <span className={`font-serif text-2xl tracking-tight ${night ? "text-[#f2f0ff]" : ""}`}>Voyance Étoilée</span>
  </span>
);

const STATUS_MAP = {
  available: { label: "Disponible", dot: "bg-green-500", text: "text-green-600" },
  busy: { label: "En consultation", dot: "bg-amber-500", text: "text-amber-600" },
  offline: { label: "Hors ligne", dot: "bg-gray-400", text: "text-gray-500" },
};

export const StatusBadge = ({ status, night = false, className = "" }) => {
  const s = STATUS_MAP[status] || STATUS_MAP.offline;
  return (
    <span data-testid={`status-${status}`} className={`inline-flex items-center gap-1.5 text-xs font-mono tracking-wider ${className}`}>
      <span className={`w-2 h-2 rounded-full ${s.dot} ${status === "available" ? "animate-pulse" : ""}`} />
      <span className={night ? "text-[#e9e6ff]" : s.text}>{s.label}</span>
    </span>
  );
};

export const Navbar = ({ overHero = false }) => {
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const isMedium = user && (user.role === "medium" || user.role === "admin");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const light = overHero && !scrolled; // light text over dark hero
  const linkCls = light ? "text-white/70 hover:text-white" : "text-[var(--muted)] hover:text-[var(--text)]";

  const links = [
    { label: "Médiums", href: "/#mediums" },
    { label: "Astrologie", href: "/astrologie" },
    { label: "Tirage gratuit", href: "/tirage-oracle" },
    { label: "Forfaits", href: "/#forfaits" },
    { label: "Avis", href: "/#avis" },
    { label: "Blog", href: "/blog" },
    { label: "FAQ", href: "/faq" },
  ];

  return (
    <header data-testid="navbar" className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${scrolled ? "glass" : "bg-transparent"}`}>
      <AnnouncementBar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link"><BrandMark night={light} /></Link>

        <nav className="hidden lg:flex items-center gap-6 text-sm">
          {links.map((l) => (
            <a key={l.label} href={l.href} className={`link-underline transition-colors ${(l.label === "Astrologie" || l.label === "Tirage gratuit") ? "text-gold font-medium" : linkCls}`}>{l.label}</a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          {user ? (
            <>
              {isMedium ? (
                <Link to="/medium" data-testid="nav-medium" className="text-sm text-gold font-mono tracking-wider">ESPACE MÉDIUM</Link>
              ) : (
                <Link to="/espace" data-testid="nav-espace" className="text-sm text-gold font-mono tracking-wider">
                  {fmtEuros(user.credit_cents)} · MON ESPACE
                </Link>
              )}
              <button data-testid="nav-logout" onClick={async () => { await logout(); navigate("/"); }} className={`px-4 py-2 rounded-full text-sm ${light ? "btn-ghost-night" : "btn-ghost"}`}>Déconnexion</button>
            </>
          ) : (
            <>
              <Link to="/connexion" data-testid="nav-login" className={`text-sm transition-colors ${light ? "text-white/70 hover:text-white" : "text-[var(--muted)] hover:text-[var(--text)]"}`}>Connexion</Link>
              <Link to="/inscription" data-testid="nav-register" className="btn-gold px-5 py-2.5 rounded-full text-sm font-medium">Commencer</Link>
            </>
          )}
        </div>

        <button data-testid="mobile-menu-btn" className={light ? "lg:hidden text-white" : "lg:hidden text-[var(--text)]"} onClick={() => setOpen(!open)}>{open ? <X /> : <Menu />}</button>
      </div>

      {open && (
        <div className="lg:hidden glass px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (<a key={l.label} href={l.href} onClick={() => setOpen(false)} className="text-[var(--muted)]">{l.label}</a>))}
          {user ? (
            <>
              {isMedium ? (
                <Link to="/medium" onClick={() => setOpen(false)} className="text-gold">Espace médium</Link>
              ) : (
                <Link to="/espace" onClick={() => setOpen(false)} className="text-gold">Mon espace</Link>
              )}
              <button onClick={async () => { await logout(); navigate("/"); }} className="text-left text-[var(--muted)]">Déconnexion</button>
            </>
          ) : (
            <>
              <Link to="/connexion" onClick={() => setOpen(false)} className="text-[var(--muted)]">Connexion</Link>
              <Link to="/inscription" onClick={() => setOpen(false)} className="btn-gold px-5 py-2.5 rounded-full text-sm text-center">Commencer</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
};

const FooterCol = ({ title, links }) => (
  <div className="text-sm">
    <p className="font-mono text-xs tracking-[0.2em] text-goldsoft mb-4">{title}</p>
    <div className="space-y-2.5">
      {links.map(([label, to, tid]) => (
        <Link key={tid} to={to} data-testid={tid} className="block text-[#e9e6ff] hover:text-goldsoft transition-colors link-underline">{label} →</Link>
      ))}
    </div>
  </div>
);

export const Footer = () => (
  <footer className="night night-stars relative overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr_1fr] relative z-10">
      <div>
        <BrandMark night className="mb-4" />
        <p className="text-muted-night italic font-serif text-lg leading-relaxed">« Sous les étoiles, éclairez votre chemin. »</p>
        <p className="text-muted-night text-sm max-w-xs leading-relaxed mt-3">Voyance sincère et confidentielle avec nos médiums. Par téléphone ou par chat.</p>
        <p className="text-2xl font-serif text-[#f2f0ff] leading-snug mt-6">Disponibles 7j/7</p>
        <a href="/#mediums" data-testid="footer-consult-chat" className="mt-3 inline-flex items-center gap-2 text-[#f2f0ff] hover:text-goldsoft transition-colors font-medium text-sm"><MessageCircle className="w-5 h-5" /> Démarrer une consultation →</a>
      </div>
      <FooterCol title="CONSULTATIONS" links={[
        ["Voyance en ligne", "/voyance-en-ligne", "footer-voyance-ligne"],
        ["Voyance par téléphone", "/voyance-par-telephone", "footer-voyance-telephone"],
        ["Voyance par tchat", "/voyance-par-tchat", "footer-voyance-tchat"],
        ["Voyance par e-mail", "/voyance-par-email", "footer-voyance-email"],
        ["Voyance gratuite", "/voyance-gratuite", "footer-voyance-gratuite"],
      ]} />
      <FooterCol title="OUTILS GRATUITS" links={[
        ["Tirage tarot & oracle", "/tirage-oracle", "footer-tirage-oracle"],
        ["Tarot oui non", "/tarot-oui-non", "footer-tarot-oui-non"],
        ["Horoscope du jour", "/horoscope", "footer-horoscope"],
        ["Thème astral", "/theme-astral-gratuit", "footer-theme-astral"],
        ["Compatibilité amoureuse", "/compatibilite", "footer-compatibilite"],
        ["Numérologie", "/numerologie", "footer-numerologie"],
      ]} />
      <FooterCol title="AIDE & INFOS" links={[
        ["Qui sommes-nous ?", "/qui-sommes-nous", "footer-qui-sommes-nous"],
        ["Blog", "/blog", "footer-blog"],
        ["Questions fréquentes", "/faq", "footer-faq"],
        ["Avis clients", "/#avis", "footer-avis"],
        ["Comment nous vérifions les avis", "/avis-verification", "footer-avis-verification"],
        ["Devenir médium — Postuler", "/postuler", "footer-postuler"],
      ]} />
    </div>
    {/* Liens secondaires : une seule ligne discrète au lieu de longues colonnes */}
    <div className="border-t border-white/10 relative z-10">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-muted-night/80">
        {[["Voyance sans CB", "/voyance-sans-cb"], ["Voyance pas chère", "/voyance-pas-chere"], ["Voyance audiotel", "/voyance-audiotel"],
          ["Tirage amoureux gratuit", "/tirage-amoureux-gratuit"], ["Tarot de Marseille", "/tirage-tarot-de-marseille"],
          ["Guide Oracle de Belline", "/guide-oracle-de-belline"], ["Accès médium", "/acces-medium"]].map(([label, to]) => (
          <Link key={to} to={to} className="hover:text-goldsoft transition-colors">{label}</Link>
        ))}
      </div>
    </div>
    <div className="border-t border-white/10 py-6 relative z-10 space-y-3 text-center">
      <p className="text-xs text-muted-night flex flex-wrap justify-center gap-x-5 gap-y-1.5">
        <Link to="/mentions-legales" data-testid="footer-mentions-legales" className="hover:text-goldsoft transition-colors">Mentions légales</Link>
        <Link to="/confidentialite" data-testid="footer-confidentialite" className="hover:text-goldsoft transition-colors">Confidentialité</Link>
        <Link to="/cookies" data-testid="footer-cookies" className="hover:text-goldsoft transition-colors">Cookies</Link>
        <Link to="/conditions-generales" data-testid="footer-cgv" className="hover:text-goldsoft transition-colors">Conditions Générales</Link>
      </p>
      <p className="text-xs text-muted-night">Paiement 100% sécurisé · Consultations confidentielles · Forfaits sans engagement</p>
      <p className="text-xs text-muted-night font-mono tracking-wider">© 2026 VOYANCE ÉTOILÉE — TOUS DROITS RÉSERVÉS</p>
    </div>
  </footer>
);
